import i18next, { i18n } from 'i18next';
import { createContext, ReactNode, useContext } from 'react';

import { mockCtx } from './mockContext';
import { TranslateFnType, TranslateFnTypeReact } from './types';
import { getTranslateFn, translateReact } from './utils';

type TranslationContextType = {
  i18n: i18n;
  namespace: string;
  t: TranslateFnType;
  t__react: TranslateFnTypeReact;
};

type ProviderProps = {
  namespace: string;
  children: ReactNode;
  translations?: Record<string, unknown>;
};

const TranslationContext = createContext<TranslationContextType | undefined>(
  undefined,
);

export const RequireTranslations = ({
  children,
  namespace,
  translations,
}: ProviderProps) => {
  const translateFn = getTranslateFn(namespace, translations);

  const reactTranslateFn = (key: string, options: Record<string, ReactNode>) =>
    translateReact(translateFn, key, options);

  const value = {
    i18n: i18next,
    namespace,
    t: translateFn,
    t__react: reactTranslateFn,
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export function useTranslation() {
  const context = useContext(TranslationContext);

  /**
   * Just return translation key instead of translating in test env (jest unit tests)
   * if we didn'n wrap a component using useTranslation hook in RequireTranslations provider
   */
  if (context === undefined && process.env.NODE_ENV === 'test') {
    const ctx = createContext(mockCtx);
    return useContext(ctx);
  }

  if (context === undefined) {
    throw new Error('useTranslation must be used within a RequireTranslations');
  }

  return context;
}
